import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';

import { AccountsService } from '../../services/accounts.service';
import { PermissionService } from '../../services/permissions.service';
import {
  EntitlementReason,
  isKnownEntitlementReason,
} from '../../guards/billing-lock.guard';
import { Session, accountInfo } from '../../services/session.service';
import { HeaderComponent } from '../shared/header/header.component';
import { FooterComponent } from '../shared/footer/footer.component';
import { WeaveButtonComponent } from '../../shared/forms/weave-button/weave-button.component';
import { AccountManagementModalComponent } from '../subscription/account-management-modal/account-management-modal.component';

/** An account admin, as `/app/accounts/status` names them while locked out. */
interface AccountAdmin {
  name: string;
  email: string;
}

/** What the customer is told, and what they are asked to do about it. */
interface LockedCopy {
  heading: string;
  body: string;
  action: string;
  /**
   * 🔴 Which action actually works for this reason — they are NOT the same.
   *
   * `subscribe` routes to `/subscription/new`, the ONLY caller of
   * `POST /app/membership/subscribe`. `card` opens the account modal, which
   * captures a payment method and does not subscribe.
   *
   * An earlier version sent all three to the modal and asserted in a comment
   * that it was "the canonical payment UI". It is the canonical CARD-MANAGEMENT
   * UI: a lapsed trial and a cancelled account clicked Subscribe, got a modal
   * with no subscribe control, and had nowhere else to go.
   */
  does: 'subscribe' | 'card';
}

/**
 * 🔴 One entry per reason, and they are NOT interchangeable.
 *
 * WEA-1070 plumbed `reason` through `HttpExceptionFilter` specifically so a
 * surface that needed the distinction would not have to change three layers to
 * get it, and recorded that nothing branched on it yet. This is that surface.
 *
 * A lapsed trial never paid us and needs to subscribe. A failed card HAS paid
 * and needs to fix one thing. A cancellation was the customer's own choice and
 * must not be scolded for it. Collapsing these into one sentence is what the
 * old global 402 modal did.
 */
const COPY: Record<EntitlementReason, LockedCopy> = {
  trial_expired: {
    heading: 'Your free trial has ended',
    body: 'Subscribe to get back into your account. Everything you have added is still here.',
    action: 'Subscribe',
    does: 'subscribe',
  },
  /**
   * 🔴 NAMES NO MECHANISM, after four rewrites that each did and each turned out
   * false in some state: "reopens straight away", "we will retry the payment",
   * "contact support", "Stripe retries automatically", "we will put the payment
   * through". Whoever settles it — our retry on card save, Stripe's own
   * schedule, or the customer on the hosted invoice — the condition is the same
   * and this sentence stays true.
   */
  past_due: {
    heading: 'Your last payment did not go through',
    body:
      'Update your card. Your account reopens as soon as the payment clears, ' +
      'and nothing has been lost.',
    action: 'Update your card',
    does: 'card',
  },
  canceled: {
    heading: 'This subscription has ended',
    body: 'Subscribe again to reopen your account. Everything you have added is still here.',
    action: 'Subscribe again',
    does: 'subscribe',
  },
};

/**
 * The billing-only shell (WEA-1141).
 *
 * WEA-1140 made the lockout enforceable — every route but the pay-us surface
 * answers 402. This is what the customer sees instead of that: one reason, one
 * action, and the way out if they would rather leave.
 *
 * 🔴 It reuses existing surfaces rather than growing its own card form —
 * re-deriving card capture is how this epic produced the same defects three
 * times over (WEA-1066 took eleven review rounds doing exactly that). Which
 * surface depends on the reason; see `LockedCopy.does`. The modal manages CARDS
 * and cannot subscribe, which round 1 of this story learned the hard way.
 */
@Component({
  selector: 'app-billing-locked',
  standalone: true,
  imports: [HeaderComponent, FooterComponent, WeaveButtonComponent],
  template: `
    <app-header></app-header>
    <main class="locked">
      @if (copy(); as c) {
        <h1>{{ c.heading }}</h1>
        <p class="body">{{ c.body }}</p>
        @if (canManageBilling()) {
          <div class="actions">
            <app-weave-button [text]="c.action" (clicked)="act(c.does)"></app-weave-button>
          </div>
        } @else {
          <!-- 🔴 Every /app/membership/* route is @Roles('accountAdmin'), so for a
               non-admin these buttons would 403 one after another. Say who can
               fix it instead of offering an action that cannot work. -->
          <p class="body">
            An account administrator can reopen this account from their own login.
          </p>
          <!-- Who that is. Every billing route is admin-only, so a member told
               only "an account administrator" had no way to find one. -->
          @if (admins().length) {
            <ul class="admins">
              @for (admin of admins(); track admin.email) {
                <li>
                  {{ admin.name || admin.email }}
                  @if (admin.name && admin.email) {
                    &middot; <a [href]="'mailto:' + admin.email">{{ admin.email }}</a>
                  }
                </li>
              }
            </ul>
          }
        }
        <!-- 🔴 Offered to anyone who CAN act on it. WEA-1140 exempts cancel from
             the lockout on purpose — a locked-out customer who wants to leave
             must be able to, without contacting support — but the route behind
             it is admin-only, so showing it to a non-admin offers a 403. They
             are told who can act instead. (No backticks in here: this is a
             template literal.) -->
        @if (canManageBilling()) {
          <!-- 🔴 Names BOTH things behind this link. It is the only self-service
               route to the invoices — including the hosted page a customer needs
               when our retry declines — and labelling it solely as closing the
               account hid that behind a sentence about leaving. -->
          <p class="leave">
            <a role="button" tabindex="0" (click)="openBilling()" (keyup.enter)="openBilling()"
              >Manage your subscription</a
            >
            to see your invoices, or to close the account.
          </p>
        }
        <!-- Only this account is locked; another may be fine. The switcher lives
             in the header above, but nothing here said so. -->
        <p class="leave">If you belong to another account, switch to it from the menu above.</p>
      } @else {
        <!-- Fails OPEN: no reason means this screen should not have been
             reached at all, so send them back rather than assert anything. -->
        <h1>Your account is available</h1>
        <div class="actions">
          <app-weave-button text="Continue" (clicked)="leave()"></app-weave-button>
        </div>
      }
    </main>
    <app-footer></app-footer>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .locked {
      max-width: 560px;
      margin: 64px auto;
      padding: 0 24px;
      text-align: center;
    }
    .body {
      color: #424242;
      margin-bottom: 28px;
    }
    .actions {
      display: flex;
      justify-content: center;
      gap: 12px;
    }
    .admins {
      list-style: none;
      padding: 0;
      margin: -16px 0 28px;
      color: #424242;
    }
    .admins a {
      color: inherit;
      text-decoration: underline;
    }
    .leave {
      margin-top: 32px;
      font-size: 0.9rem;
      color: #424242;
    }
    .leave a {
      cursor: pointer;
      text-decoration: underline;
    }
  `,
})
export class BillingLockedComponent {
  private readonly accounts = inject(AccountsService);
  private readonly session = inject(Session);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * 🔴 Same gate the right menu applies to Manage Account. Without it a
   * non-admin team member on a locked-out account is shown a payment prompt
   * whose every button 403s, with no route out.
   */
  protected readonly canManageBilling = computed(() =>
    !!this.permissions.getPermission('accountPermissions', 'manageAccount'),
  );

  private readonly reason = signal<EntitlementReason | null>(null);
  /** The account's admins, from the same status read — sent only while locked. */
  protected readonly admins = signal<AccountAdmin[]>([]);
  protected readonly copy = computed<LockedCopy | null>(() => {
    const r = this.reason();
    return r ? COPY[r] : null;
  });

  constructor() {
    this.accounts
      .getAccountStatusCached(this.identityKey())
      .pipe(first())
      .subscribe({
        next: (resp) => {
          const data = resp?.success ? resp.data?.[0]?.dataSet : null;
          const reason = data?.entitlementReason ?? null;
          this.reason.set(isKnownEntitlementReason(reason) ? reason : null);
          this.admins.set(Array.isArray(data?.accountAdmins) ? data.accountAdmins : []);
        },
        // Fail open: a status we could not read must not become a lockout.
        error: () => this.reason.set(null),
      });
  }

  /** Same key as the header, right menu and the guard — a shared cache hit. */
  private identityKey(): string {
    const user = this.session.getUser() as
      | { userRequestKey?: string; userData?: { currentUserAccount?: accountInfo } }
      | null;
    const accountKey = user?.userData?.currentUserAccount?.accountRequestKey ?? '';
    return `${accountKey}|${user?.userRequestKey ?? ''}`;
  }

  /** Routes to whichever surface can actually resolve THIS reason. */
  protected act(does: 'subscribe' | 'card'): void {
    if (does === 'subscribe') {
      // `/subscription/new` — the only caller of POST /app/membership/subscribe.
      void this.router.navigate(['/subscription', 'new']);
      return;
    }
    this.openBilling();
  }

  protected openBilling(): void {
    this.dialog
      .open(AccountManagementModalComponent, { width: '600px', disableClose: false })
      .afterClosed()
      // `takeUntilDestroyed` as well as `first()`, belt and braces.
      //
      // ⚠️ The obvious motive — "the modal outlives this component on an account
      // switch" — is weaker than it looks: `MatDialogConfig.closeOnNavigation`
      // defaults to true, so a routing switch closes the dialog and fires
      // `afterClosed` BEFORE destroy. This guards the non-routing destructions
      // instead, and costs nothing.
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // 🔴 RESET BEFORE RE-READING, or nothing is ever released.
        //
        // `getAccountStatusCached` is `shareReplay({bufferSize: 1,
        // refCount: false})` keyed on identity, and paying does not change
        // identity — its own doc says a same-identity billing change must call
        // this explicitly. Re-reading first replays the stale reason forever.
        this.accounts.resetAccountStatusCache();
        this.refresh();
      });
  }

  /**
   * Re-read the lockout, and render whatever is now true.
   *
   * 🔴 DELIBERATELY DUMB, and three review rounds got here the long way.
   *
   * Earlier versions tried to tell the customer what had just happened —
   * navigate away on success, show a bespoke "your card was saved but the
   * payment has not gone through" otherwise. The component cannot know that.
   * `afterClosed()` fires identically for a saved card, a dismissed backdrop and
   * the cancel-subscription path, so the message was shown to people who had
   * saved nothing and to people who were closing their account. And because
   * nothing settles the invoice synchronously, it fired for EVERY past-due card
   * update rather than as a rare race.
   *
   * So it asks one question — am I still locked out — and renders the answer.
   * Still locked shows the same accurate reason as before; no longer locked
   * leaves. A stale reason for a few seconds is mildly wrong; the alternative
   * was confidently wrong in three directions at once.
   */
  private refresh(): void {
    this.accounts
      .getAccountStatusCached(this.identityKey())
      .pipe(first())
      .subscribe({
        next: (resp) => {
          const data = resp?.success ? resp.data?.[0]?.dataSet : null;
          const reason = data?.entitlementReason ?? null;
          if (!reason) {
            void this.router.navigate(['/home']);
            return;
          }
          this.reason.set(isKnownEntitlementReason(reason) ? reason : null);
        },
        // Fail open: a status we could not read must not strand anyone here.
        error: () => void this.router.navigate(['/home']),
      });
  }

  protected leave(): void {
    void this.router.navigate(['/home']);
  }
}
